import { Combobox } from "@/components/ui/combobox";
import { Select } from "@/components/ui/select";
import { displayStateName } from "@/lib/workflow";
import { useNavigate, useRouterState } from "@tanstack/react-router";

/**
 * Filter row for the work-items list. Port of the Next
 * `filter-bar.tsx`. Native `<select>` for the fixed enums
 * (status / priority / type / scope / attachments) and a
 * searchable `<Combobox>` for the entity sets (assignee / reporter
 * / watcher / project).
 *
 * Framework swaps: `next/navigation` -> TanStack `useRouterState`
 * for the live search params + `useNavigate` for the writes. Every
 * filter change also strips `?page=` so a refined filter resets
 * pagination back to page 1.
 */

type Option = { id: string; name: string };

type FilterBarProps = {
  statuses: Option[];
  types: Option[];
  priorities: Array<{ value: string; label: string }>;
  users: Option[];
  projects: Option[];
  /** Releases of the scoped project — open, locked and closed — the filter
   *  only renders when the list is locked to one project (releases are
   *  project-scoped). */
  releases?: Array<{ id: string; name: string; status: string }>;
};

export function FilterBar({
  statuses,
  types,
  priorities,
  users,
  projects,
  releases = [],
}: FilterBarProps) {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;

  function update(key: string, value: string) {
    navigate({
      // @ts-expect-error generic search updater — TanStack typed routes
      // can't statically validate a filter parameterised by a key.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        [key]: value || undefined,
        page: undefined,
      }),
      replace: true,
    });
  }

  const get = (key: string): string => (typeof search[key] === "string" ? search[key] : "");

  const selectClass = "min-w-[140px]";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        className={selectClass}
        value={get("status")}
        onChange={(v) => update("status", v)}
        aria-label="Filter by status"
        options={[
          { value: "", label: "All statuses" },
          ...statuses.map((s) => ({ value: s.id, label: displayStateName(s.name) })),
        ]}
      />

      <Select
        className={selectClass}
        value={get("priority")}
        onChange={(v) => update("priority", v)}
        aria-label="Filter by priority"
        options={[{ value: "", label: "All priorities" }, ...priorities]}
      />

      <Select
        className={selectClass}
        value={get("type")}
        onChange={(v) => update("type", v)}
        aria-label="Filter by type"
        options={[
          { value: "", label: "All types" },
          ...types.map((t) => ({ value: t.id, label: t.name })),
        ]}
      />

      <Combobox
        className="min-w-[160px]"
        value={get("assignee")}
        onChange={(v) => update("assignee", v)}
        placeholder="All assignees"
        emptyLabel="No users"
        aria-label="Filter by assignee"
        clearable
        options={[
          // The unclaimed pool — a work item nobody picked up.
          { value: "none", label: "Unassigned" },
          ...users.map((u) => ({ value: u.id, label: u.name })),
        ]}
      />

      {/* Reporter dropdown mirrors the "Reported by me" pill — picking
          a user is equivalent to the pill but lets an admin pick
          someone else. */}
      <Combobox
        className="min-w-[160px]"
        value={get("author")}
        onChange={(v) => update("author", v)}
        placeholder="All reporters"
        emptyLabel="No users"
        aria-label="Filter by reporter"
        clearable
        options={users.map((u) => ({ value: u.id, label: u.name }))}
      />

      <Combobox
        className="min-w-[160px]"
        value={get("watcher")}
        onChange={(v) => update("watcher", v)}
        placeholder="All watchers"
        emptyLabel="No users"
        aria-label="Filter by watcher"
        clearable
        options={users.map((u) => ({ value: u.id, label: u.name }))}
      />

      {projects.length > 0 && (
        <Combobox
          className="min-w-[180px]"
          value={get("project")}
          onChange={(v) => update("project", v)}
          placeholder="All projects"
          emptyLabel="No projects"
          aria-label="Filter by project"
          clearable
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
        />
      )}

      {/* Environment — where the change currently lives (stamped by the
          workflow when a WI enters an env's testing step). */}
      <Select
        className={selectClass}
        value={get("environment")}
        onChange={(v) => update("environment", v)}
        aria-label="Filter by environment"
        options={[
          { value: "", label: "All environments" },
          { value: "dev", label: "On dev" },
          { value: "test", label: "On test" },
          { value: "main", label: "On production" },
          { value: "none", label: "Not deployed" },
        ]}
      />

      {releases.length > 0 && (
        <Select
          className={selectClass}
          value={get("release")}
          onChange={(v) => update("release", v)}
          aria-label="Filter by release"
          options={[
            { value: "", label: "All releases" },
            { value: "none", label: "No release" },
            ...releases.map((r) => ({
              value: r.id,
              label: r.status === "open" ? r.name : `${r.name} (${r.status})`,
            })),
          ]}
        />
      )}

      {/* Scope toggle — mirrors the "Open" pill + the server's scope
          semantics: blank = default (devs see Mine, admins see open),
          open = every open WI, all = include closed. */}
      <Select
        className={selectClass}
        value={get("scope")}
        onChange={(v) => update("scope", v)}
        aria-label="Filter by scope"
        options={[
          { value: "", label: "Default scope" },
          { value: "open", label: "Open only" },
          { value: "all", label: "Include closed" },
        ]}
      />

      {/* Attachments — narrows to WIs carrying at least one file. */}
      <Select
        className={selectClass}
        value={get("attachments")}
        onChange={(v) => update("attachments", v)}
        aria-label="Filter by attachments"
        options={[
          { value: "", label: "Any attachments" },
          { value: "any", label: "With attachments" },
        ]}
      />
    </div>
  );
}
